import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useMemberAuth } from "@/lib/memberAuth";
import { toast } from "sonner";
import { SignIn, Lock } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

export default function MemberLogin() {
  const { login, member } = useMemberAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (member) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to the EPAS member portal.");
      navigate("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-6 relative overflow-hidden" data-testid="member-login-page">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="relative w-full max-w-md bg-white p-10 border border-black/5 shadow-2xl">
        <div className="mb-8">
          <Logo size="md" asLink={false} showTagline={false} testId="portal-login-logo" />
          <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-navy/50">Member Portal</div>
        </div>
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight">Welcome back</h1>
        <p className="text-sm text-navy/60 mt-2">Members-only access — directory, events, introductions.</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="overline mb-2 block">Email</label>
            <input
              data-testid="member-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
              required
            />
          </div>
          <div>
            <label className="overline mb-2 block">Password</label>
            <input
              data-testid="member-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="member-login-submit"
            className="btn-primary w-full justify-center"
          >
            <SignIn size={16} />
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div className="mt-6 flex items-center justify-between text-xs">
          <Link to="/portal/forgot-password" data-testid="portal-forgot-link" className="text-navy/60 hover:text-ember flex items-center gap-1">
            <Lock size={12} /> Forgot password
          </Link>
          <Link to="/" className="text-navy/50 hover:text-navy uppercase tracking-widest" data-testid="portal-back-home">
            Back to site
          </Link>
        </div>
        <p className="mt-6 text-[11px] text-navy/50 leading-relaxed">
          Not a member yet?{" "}
          <Link to="/apply" className="text-navy hover:text-ember underline">
            Apply for membership
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
